// Page de connexion membre avec layout divisé et gestion NIP améliorée

import React, { useState } from 'react';
import {
    Lock,
    LogIn,
    X,
    CreditCard,
    ArrowLeft
} from 'lucide-react';

import { useMembership } from '../hooks/useMembership';

interface AuthFormData {
    lastName: string;
    nip: string;
    loginMethod: 'nip' | 'secure';
    memberNumber: string;
    dateOfBirth: string;
}

interface MemberLoginPageProps {
    onAuthenticated: (memberData: any) => void;
    onBack?: () => void;
}

const MemberLoginPage: React.FC<MemberLoginPageProps> = ({ onAuthenticated, onBack }) => {
    const [authForm, setAuthForm] = useState<AuthFormData>({
        lastName: '',
        nip: '',
        loginMethod: 'secure',
        memberNumber: '',
        dateOfBirth: ''
    });
    
    const [loginAttempts, setLoginAttempts] = useState(0);
    const [isBlocked, setIsBlocked] = useState(false);
    const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);

    const {
        getMemberByNipAndLastName,
        getMemberBySecureCredentials,
        isLoading,
        error,
        clearError
    } = useMembership();

    // Gestion du blocage temporaire
    React.useEffect(() => {
        if (isBlocked && blockTimeRemaining > 0) {
            const timer = setTimeout(() => {
                setBlockTimeRemaining(prev => {
                    if (prev <= 1) {
                        setIsBlocked(false);
                        setLoginAttempts(0);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [isBlocked, blockTimeRemaining]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        // Validation spéciale pour le nom de famille
        if (name === 'lastName') {
            // Permettre seulement les lettres, espaces, apostrophes et tirets
            const filteredValue = value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '');
            setAuthForm(prev => ({ ...prev, [name]: filteredValue }));
        } else {
            setAuthForm(prev => ({ ...prev, [name]: value }));
        }
        
        clearError();
    };

    // Formatage du NIP avec gestion du backspace améliorée
    const formatNip = (value: string): string => {
        // Garder seulement les caractères alphanumériques
        const alphanumeric = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const limited = alphanumeric.slice(0, 14);

        if (limited.length >= 10) {
            return `${limited.slice(0, 2)}-${limited.slice(2, 6)}-${limited.slice(6)}`;
        } else if (limited.length >= 6) {
            return `${limited.slice(0, 2)}-${limited.slice(2, 6)}-${limited.slice(6)}`;
        } else if (limited.length >= 2) {
            return `${limited.slice(0, 2)}-${limited.slice(2)}`;
        }
        return limited;
    };

    // Gestion spéciale du NIP avec backspace
    const handleNipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        const cursorPosition = e.target.selectionStart || 0;
        const previousValue = authForm.nip;

        // Si on supprime et qu'on est juste après un tiret, supprimer aussi le caractère précédent
        if (newValue.length < previousValue.length) {
            // On est en train de supprimer
            if (previousValue[cursorPosition] === '-' && cursorPosition > 0) {
                // On était juste après un tiret, supprimer aussi le caractère avant le tiret
                const withoutDashes = previousValue.replace(/[^A-Za-z0-9]/g, '');
                const newWithoutDashes = withoutDashes.slice(0, -1);
                const formatted = formatNip(newWithoutDashes);
                setAuthForm(prev => ({ ...prev, nip: formatted }));
                return;
            }
        }

        // Formatage normal
        const formattedValue = formatNip(newValue);
        setAuthForm(prev => ({ ...prev, nip: formattedValue }));
        clearError();
    };

    // Gestion des touches spéciales pour le NIP
    const handleNipKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        const cursorPosition = target.selectionStart || 0;
        const value = target.value;

        // Si on appuie sur Backspace et qu'on est juste après un tiret
        if (e.key === 'Backspace' && cursorPosition > 0 && value[cursorPosition - 1] === '-') {
            e.preventDefault();
            // Supprimer le caractère avant le tiret
            const withoutDashes = value.replace(/[^A-Za-z0-9]/g, '');
            const newWithoutDashes = withoutDashes.slice(0, -1);
            const formatted = formatNip(newWithoutDashes);
            setAuthForm(prev => ({ ...prev, nip: formatted }));

            // Repositionner le curseur
            setTimeout(() => {
                const newCursorPos = Math.max(0, cursorPosition - 2);
                target.setSelectionRange(newCursorPos, newCursorPos);
            }, 0);
        }
    };


    const handleFailedAttempt = () => {
        const newAttempts = loginAttempts + 1;
        setLoginAttempts(newAttempts);
        
        if (newAttempts >= 3) {
            setIsBlocked(true);
            setBlockTimeRemaining(300); // 5 minutes
        }
    };

    const handleAuthentication = async () => {
        clearError();

        if (isBlocked) {
            alert(`Trop de tentatives. Réessayez dans ${Math.ceil(blockTimeRemaining / 60)} minutes.`);
            return;
        }

        // Validation renforcée du nom de famille
        const lastName = authForm.lastName.trim();
        if (!lastName) {
            alert('Veuillez remplir le nom de famille');
            return;
        }
        
        if (lastName.length < 2) {
            alert('Le nom de famille doit contenir au moins 2 caractères');
            return;
        }
        
        // Vérifier qu'il n'y a pas que des espaces ou caractères spéciaux
        if (!/^[a-zA-ZÀ-ÿ\s'-]+$/.test(lastName)) {
            alert('Le nom de famille ne peut contenir que des lettres, espaces, apostrophes et tirets');
            return;
        }
        
        // Vérifier qu'il y a au moins une lettre
        if (!/[a-zA-ZÀ-ÿ]/.test(lastName)) {
            alert('Le nom de famille doit contenir au moins une lettre');
            return;
        }

        let member = null;

        if (authForm.loginMethod === 'nip') {
            if (!authForm.nip.trim()) {
                alert('Veuillez remplir le N° NIP');
                return;
            }

            const nipPattern = /^[A-Za-z0-9]{2}-[A-Za-z0-9]{4}-[A-Za-z0-9]{8}$/;
            if (!nipPattern.test(authForm.nip)) {
                alert('Format du N° NIP invalide (XX-XXXX-XXXXXXXX)');
                return;
            }

            member = await getMemberByNipAndLastName(
                authForm.nip.trim(),
                authForm.lastName.trim()
            );
        } else {
            // Méthode sécurisée avec 3 facteurs
            if (!authForm.memberNumber.trim() || !authForm.dateOfBirth.trim()) {
                alert('Veuillez remplir tous les champs requis');
                return;
            }

            member = await getMemberBySecureCredentials(
                authForm.lastName.trim(),
                authForm.memberNumber.trim(),
                authForm.dateOfBirth.trim()
            );
        }

        if (member) {
            // Succès - réinitialiser les tentatives
            setLoginAttempts(0);
            setIsBlocked(false);
            onAuthenticated(member);
        } else {
            // Échec - comptabiliser la tentative
            handleFailedAttempt();
        }
    };

    // Validation du formulaire
    const isFormValid = () => {
        const lastName = authForm.lastName.trim();
        
        // Validation du nom de famille
        if (!lastName || lastName.length < 2 || !/^[a-zA-ZÀ-ÿ\s'-]+$/.test(lastName) || !/[a-zA-ZÀ-ÿ]/.test(lastName)) {
            return false;
        }
        
        // Validation selon la méthode de connexion
        if (authForm.loginMethod === 'nip') {
            return authForm.nip.trim().length > 0;
        } else {
            return authForm.memberNumber.trim().length > 0 && authForm.dateOfBirth.trim().length > 0;
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && isFormValid()) {
            handleAuthentication();
        }
    };

    return (
        <div className="min-h-screen flex pt-16">
            {/* Image à gauche */}
            <div className="hidden lg:block lg:w-1/2 relative" style={{height: 'calc(100vh - 4rem)'}}>
                <img
                    src="/images/loginimg.webp"
                    alt="Union Démocratique des Bâtisseurs"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Overlay pour améliorer la lisibilité si besoin */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-green-900/20"></div>

                {/* Logo/Texte sur l'image */}
                <div className="absolute bottom-8 left-8 text-white">
                    <h1 className="text-4xl font-bold mb-2">Union Démocratique</h1>
                    <h2 className="text-3xl font-bold mb-4">des Bâtisseurs</h2>
                    <p className="text-lg opacity-90">Accédez à votre espace membre</p>
                </div>
            </div>

            {/* Formulaire à droite */}
            <div className="w-full lg:w-1/2 flex items-start justify-center p-8 pt-16 bg-gray-50">
                <div className="w-full max-w-md">
                    {/* Bouton retour */}
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>Retour</span>
                        </button>
                    )}

                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="flex items-center justify-center space-x-3 mb-6 lg:hidden">
                            {/* Logo mobile */}
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-green-600 flex items-center justify-center">
                                <span className="text-white font-bold text-lg">UDB</span>
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">Union Démocratique</h1>
                                <p className="text-gray-600 text-sm">des Bâtisseurs</p>
                            </div>
                        </div>

                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Espace Membre</h2>
                        <p className="text-gray-600">Connectez-vous à votre compte</p>
                    </div>

                    {/* Formulaire */}
                    <div className="bg-white p-8 shadow-lg border border-gray-200">
                        <div className="flex items-center space-x-3 mb-8">
                            <Lock className="w-6 h-6 text-blue-600" />
                            <h3 className="text-xl font-bold text-gray-900">Connexion</h3>
                        </div>

                        {/* Choix méthode de connexion */}
                        <div className="mb-6">
                            <p className="text-sm text-gray-600 mb-3">Méthode de connexion</p>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setAuthForm(prev => ({ ...prev, loginMethod: 'secure', nip: '' }))}
                                    className={`p-3 border rounded-lg transition-all text-sm ${
                                        authForm.loginMethod === 'secure' 
                                            ? 'bg-blue-50 border-blue-400 text-blue-700' 
                                            : 'border-gray-300 text-gray-700 hover:border-blue-300'
                                    }`}
                                >
                                    <div className="flex items-center justify-center space-x-2">
                                        <Lock className="w-4 h-4" />
                                        <span>Sans NIP</span>
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setAuthForm(prev => ({ ...prev, loginMethod: 'nip', memberNumber: '', dateOfBirth: '' }))}
                                    className={`p-3 border rounded-lg transition-all text-sm ${
                                        authForm.loginMethod === 'nip' 
                                            ? 'bg-gray-100 border-gray-400 text-gray-700' 
                                            : 'border-gray-300 text-gray-700 hover:border-gray-400'
                                    }`}
                                >
                                    <div className="flex items-center justify-center space-x-2">
                                        <CreditCard className="w-4 h-4" />
                                        <span>Avec NIP</span>
                                    </div>
                                </button>
                            </div>

                            {/* Blocage des tentatives */}
                            {isBlocked && (
                                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <div className="flex items-center space-x-2">
                                        <X className="w-4 h-4 text-red-600" />
                                        <div className="text-sm text-red-800">
                                            <p className="font-medium">Trop de tentatives</p>
                                            <p>Réessayez dans {Math.ceil(blockTimeRemaining / 60)} min</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Indicateur de tentatives */}
                            {loginAttempts > 0 && !isBlocked && (
                                <div className="mt-2 text-center">
                                    <span className="text-xs text-orange-600">
                                        {loginAttempts}/3 tentatives
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="space-y-6">
                            {/* Nom de famille */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nom de famille
                                </label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={authForm.lastName}
                                    onChange={handleInputChange}
                                    onKeyPress={handleKeyPress}
                                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-lg"
                                    placeholder="Votre nom de famille"
                                    required
                                />
                            </div>

                            {/* N° NIP - Conditionnel */}
                            {authForm.loginMethod === 'nip' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        N° NIP
                                    </label>
                                    <input
                                        type="text"
                                        name="nip"
                                        value={authForm.nip}
                                        onChange={handleNipChange}
                                        onKeyDown={handleNipKeyDown}
                                        onKeyPress={handleKeyPress}
                                        maxLength={16}
                                        className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all font-mono uppercase text-lg tracking-wider"
                                        placeholder="XX-XXXX-XXXXXXXX"
                                        required
                                    />
                                </div>
                            )}


                            {/* Méthode sécurisée - 3 facteurs */}
                            {authForm.loginMethod === 'secure' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            N° de membre
                                        </label>
                                        <input
                                            type="text"
                                            name="memberNumber"
                                            value={authForm.memberNumber}
                                            onChange={handleInputChange}
                                            onKeyPress={handleKeyPress}
                                            className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all font-mono text-lg tracking-wider"
                                            placeholder="UDB-XXXXXXXX"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Date de naissance
                                        </label>
                                        <input
                                            type="date"
                                            name="dateOfBirth"
                                            value={authForm.dateOfBirth}
                                            onChange={handleInputChange}
                                            onKeyPress={handleKeyPress}
                                            className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-lg"
                                            required
                                        />
                                    </div>
                                </>
                            )}

                            {/* Message d'erreur */}
                            {error && (
                                <div className="bg-red-50 border border-red-200 p-4">
                                    <p className="text-red-800 text-sm flex items-center">
                                        <X className="w-4 h-4 mr-2" />
                                        {error}
                                    </p>
                                </div>
                            )}

                            {/* Bouton de connexion */}
                            <button
                                onClick={handleAuthentication}
                                disabled={isLoading || isBlocked || !isFormValid()}
                                className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-4 font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-lg"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="animate-spin h-5 w-5 border-b-2 border-white"></div>
                                        <span>Vérification...</span>
                                    </>
                                ) : (
                                    <>
                                        <LogIn className="w-5 h-5" />
                                        <span>Accéder à mon espace</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Lien d'inscription */}
                        <div className="mt-8 text-center">
                            <p className="text-sm text-gray-600">
                                Pas encore membre ?
                                <a href="/#adhesion" className="text-blue-600 hover:underline ml-1 font-medium">
                                    Rejoignez-nous
                                </a>
                            </p>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberLoginPage;
